/**
 * The typed editor's optional ACCESSORY seam (#1679, ruling 54674).
 *
 * ── what this is, and what it deliberately is not ──────────────────────────
 * `FieldEditor.slot` is a REPLACEMENT seam. Its documented invariant is "a ref
 * needs a host-owned picker", and it is consumed inside exactly one `if
 * (kind === 'ref')` arm. Widening it to carry an accessory for ordinary text
 * would overload one prop with two opposite meanings — replace the control, or
 * decorate it — and would make a missing slot capable of erasing the real
 * editor. The ruling forbids that, and this file is the alternative.
 *
 * An ACTION sits BESIDE the editor and hands the host two things it cannot get
 * from `onChange`: the value as currently staged, and where the caret is. The
 * host does whatever it likes and returns the next value, which is written
 * through the binding the host already owns — so dirty and validation state are
 * the form library's, untouched, exactly as they are when a person types.
 *
 * ── capability/config driven, and entity-agnostic ──────────────────────────
 * `@solum/ui` must not know what a `service_kb_article` is. An action declares
 * an optional `capability`, and the host passes the set it permits — the same
 * shape `FindingsPanel` and `WorkContext` already use, where `null` means "this
 * host has no capability model" and an omitted capability means "this offer
 * needs none". Nothing here is keyed to an entity, a module or a kind.
 *
 * ── the invariant that makes it safe to add ────────────────────────────────
 * With no permitted actions this component renders NOTHING — not an empty
 * wrapper, not a fragment with whitespace. That is what keeps the markup, the
 * focus order and the binding byte-for-byte identical on every field that does
 * not opt in, which is a real constraint rather than a nicety: `EntityForm`'s
 * suite counts `readonly` occurrences in the SSR string, and its
 * server-default suite windows 400 characters of raw HTML after a label.
 *
 * ── MUTATION TABLE (rule 10) — each applied, restored, the red read by name
 *
 *  # | mutation                                                   | measured
 * ---+------------------------------------------------------------+-----------
 *  1 | the action reads PROPS instead of the DOM                    | **RED 1**
 *  2 | the caret is ignored; insertion always at the end            | **RED 1**
 *  3 | an unpermitted capability is DISABLED, not removed           | **RED 2**
 *  4 | `next == null` becomes `=== undefined`                       | GREEN — below
 * 4r | …the same defect written FAITHFULLY                          | **RED 1**
 *  5 | `FieldActions` returns an empty wrapper instead of `null`    | **RED 1**
 *  6 | the multiline arm keys on the KIND, not the config           | **RED 1**
 *  7 | `FieldRules` drops `multiline` again                         | GREEN — below
 *
 * ROW 4 IS A GREEN THAT MEANS THE MUTATION WAS WRONG, NOT THE TEST. Changing
 * `== null` to `=== undefined` lets `null` through to
 * `next.length` in the caret arithmetic, which THROWS inside the async handler
 * before `commit` is ever called — so nothing is written and the assertion
 * ("no commit") is satisfied by a crash. The realistic defect is null treated
 * as a VALUE, which is the guard removed AND the arithmetic made null-safe.
 * Written that way (row 4r) it reds immediately: a cancelled dialog wipes the
 * field to an empty string. A mutation that converts a guard into an exception
 * proves nothing about the guard.
 *
 * ROW 7 IS A CORRECT GREEN, and it says something about the type rather than
 * about the test. Deleting `multiline` from `FieldRules` is INERT AT RUNTIME:
 * `EntityForm` forwards `f.rules` verbatim, the object from
 * `describe_semantics` carries the key whether or not the interface names it,
 * and a missing optional property is structurally assignable either way, so
 * `tsc` stays silent too. The type is documentation and editor support, not a
 * gate — what actually protects the 25 registered columns is the end-to-end
 * render test in `EntityForm.test.tsx`, and that is where the protection
 * should be looked for.
 */
import type { ReactNode } from 'react'
import { Button } from '../controls/Button'
import s from './FieldAction.module.css'

/** Where the caret is, as the DOM reports it. */
export interface FieldSelection {
  start: number
  end: number
}

/** What an action is handed, and what it gives back. */
export interface FieldActionContext {
  /** The value as STAGED — what the person has typed, not what was loaded. */
  value: string
  /** The caret or range at the moment the action was invoked. */
  selection: FieldSelection
}

export interface FieldAction {
  /** Stable key. Also the `data-field-action` value, so a test can name one. */
  key: string
  /** The control's accessible name. There is no icon-only action here. */
  label: ReactNode
  /**
   * The capability this offer needs. Omitted = it needs none.
   *
   * A capability the host does not permit removes the control ENTIRELY rather
   * than disabling it: an offer that can never be taken is furniture, which is
   * the same call `DataGrid` makes for `onSelectionChange` and `GridBar` for
   * `onCommand`.
   */
  capability?: string
  /**
   * Produce the next value. Returning `null` means "no change" — the host
   * cancelled its own dialog, and the field must not be marked dirty for it.
   */
  run: (ctx: FieldActionContext) => string | null | Promise<string | null>
}

export interface FieldActionsProps {
  actions?: readonly FieldAction[]
  /** What the host permits. `null` = the host has no capability model. */
  permitted?: readonly string[] | null
  /** Reads the staged value and caret at the moment of the press. */
  read: () => FieldActionContext | null
  /** Writes the next value through the host's binding, staying dirty-aware. */
  commit: (next: string, caret: number) => void
  /** Names the group for a screen reader, e.g. "Policy body actions". */
  label: string
  disabled?: boolean
}

/** The offers this host permits. Exported so a host can ask before rendering. */
export function permittedActions(
  actions: readonly FieldAction[] | undefined,
  permitted: readonly string[] | null | undefined,
): readonly FieldAction[] {
  if (!actions || actions.length === 0) return []
  if (permitted == null) return actions
  const allowed = new Set(permitted)
  return actions.filter((a) => a.capability == null || allowed.has(a.capability))
}

export function FieldActions({
  actions, permitted, read, commit, label, disabled = false,
}: FieldActionsProps): ReactNode {
  const offers = permittedActions(actions, permitted)
  // NOTHING, not an empty element. See the header: the absent case has to be
  // byte-for-byte, and an empty <div> is not byte-for-byte.
  if (offers.length === 0) return null

  return (
    <div className={s.actions} role="group" aria-label={label}>
      {offers.map((action) => (
        <Button
          key={action.key}
          type="button"
          size="sm"
          variant="ghost"
          disabled={disabled}
          data-field-action={action.key}
          /* `onMouseDown` with `preventDefault` so the press does not blur the
             editor before the caret is read. A blurred textarea reports
             selectionStart 0, which would insert at the top of a policy body
             rather than where the person was working — the failure would look
             like "it inserted in the wrong place" and be blamed on the host. */
          onMouseDown={(e) => { e.preventDefault() }}
          onClick={() => {
            const ctx = read()
            if (!ctx) return
            void (async () => {
              const next = await action.run(ctx)
              // `null` is "no change" and must not dirty the form. An action
              // whose dialog was cancelled has to be indistinguishable from one
              // that was never pressed.
              if (next == null) return
              commit(next, ctx.selection.start + (next.length - ctx.value.length))
            })()
          }}
        >
          {action.label}
        </Button>
      ))}
    </div>
  )
}
